'use client';

import React, { useCallback, useState } from 'react';
import { Button, Card, CardContent, TextField, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useLogin, useSetCurrentBusinessUnit, type BusinessUnitDto } from '@/hooks/api/useAuth';
import { useNotification } from '@/contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { BusinessUnitPickerDialog } from './BusinessUnitPickerDialog';
import LoginLogo from './LoginLogo';

interface LoginFormValues {
  email: string;
  password: string;
}

const LoginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

const Login: React.FC = () => {
  const loginMutation = useLogin();
  const setBuMutation = useSetCurrentBusinessUnit();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [buPickerOpen, setBuPickerOpen] = useState(false);
  const [buChoices, setBuChoices] = useState<BusinessUnitDto[]>([]);

  const handleLogin = useCallback((values: { email: string; password: string }) => {
    loginMutation.mutate(
      {
        email: values.email.trim(),
        password: values.password.trim(),
      },
      {
        onSuccess: (data) => {
          if (data.businessUnits && data.businessUnits.length > 1) {
            setBuChoices(data.businessUnits);
            setBuPickerOpen(true);
          }
        },
        onError: (error: any) => {
          console.error("Login error", error);
          // Extract error message from various possible error structures
          const errorMessage = error?.message || 
                              error?.response?.data?.message || 
                              error?.response?.data?.error ||
                              (typeof error === 'string' ? error : null) ||
                              'Login failed. Please try again.';
          showError(errorMessage);
        },
      }
    );
  }, [loginMutation, showError]);

  const handlePickBu = (id: number) => {
    setBuMutation.mutate(id, {
      onSuccess: () => {
        setBuPickerOpen(false);
        navigate('/app/dashboard');
      },
      onError: (e: any) => {
        showError(e?.message || 'Could not set business unit');
      },
    });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100vw',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? 16 : 0,
      }}
    >
      <Card
        elevation={3}
        style={{
          width: isMobile ? '100%' : 400,
          maxWidth: 400,
          borderRadius: 16,
          border: '1px solid #e0e0e0',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}
      >
        <CardContent style={{ padding: isMobile ? 16 : 32 }}>
          <LoginLogo />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Sign In
          </Typography>
          <Typography color="textSecondary" variant="body2" gutterBottom>
            Welcome back! Please login to your account.
          </Typography>
          <Formik<LoginFormValues>
            initialValues={{ email: '', password: '' }}
            validationSchema={LoginSchema}
            onSubmit={(values, { setSubmitting }) => {
              handleLogin(values);
              setSubmitting(false);
            }}
          >
            {({ isSubmitting }) => (
              <Form>
                <div style={{ marginTop: 24 }}>
                  <Field
                    name="email"
                    as={TextField}
                    label="Email"
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <ErrorMessage name="email">
                    {msg => <div style={{ color: '#d32f2f', fontSize: 13, marginTop: 4 }}>{msg}</div>}
                  </ErrorMessage>
                </div>
                <div style={{ marginTop: 16 }}>
                  <Field
                    name="password"
                    as={TextField}
                    label="Password"
                    size="small"
                    fullWidth
                    variant="outlined"
                    type="password"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <ErrorMessage name="password">
                    {msg => <div style={{ color: '#d32f2f', fontSize: 13, marginTop: 4 }}>{msg}</div>}
                  </ErrorMessage>
                </div>
                <div style={{ marginTop: 24 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    disabled={isSubmitting || loginMutation.isPending}
                    style={{ borderRadius: 8, fontWeight: 600 }}
                  >
                    Login
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
          {/* Social Login Buttons */}
          {/* <div style={{ marginTop: 16 }}>
            <Button onClick={() => handleSocialLogin('google')} variant="outlined" fullWidth>
              Login with Google
            </Button>
          </div> */}
        </CardContent>
      </Card>
      <BusinessUnitPickerDialog
        open={buPickerOpen}
        businessUnits={buChoices}
        onSelect={handlePickBu}
        isLoading={setBuMutation.isPending}
      />
    </div>
  );
};

export default Login;